import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from '@mui/material';
import actions from '@src/redux/actions';
import { ONBOARDING_POPUP_TYPE } from '@src/constants';
import { trackEducatorsHomeViewed } from '@src/services/moengage';
import theme from '@src/styles/theme';
import Header from './Header';
import Banner from './Banner';
import TeachingVoices from './TeachingVoices';
import Features from './Features';
import RecentProjects from './RecentProjects';
import SurveyPopup from './SurveyPopup';
import MobileWeb from './MobileWeb';
import { StyledEducatorsHome } from './index.style';

const EducatorsHome = () => {
  const dispatch = useDispatch();
  const { isVisible } = useSelector((state) => state.educatorsSurvey);
  const userId = useSelector((state) => state.auth.user?.id);
  // noSsr, because this decides more than what to draw. Without it the first
  // render always reports a desktop and only an effect corrects it, which is a
  // render too late for the once-per-device popup below: it would have opened
  // and spent its key on a phone before anyone knew it was a phone.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });

  // Mobile shows the unsupported screen — that visit is not a home view
  useEffect(() => {
    if (!isMobile) trackEducatorsHomeViewed();
  }, [isMobile]);

  // Welcome popup, once per user per device. Same storage key convention as
  // the other onboarding popups in useOnboardingPopup. Held back on mobile,
  // which shows the unsupported screen instead — letting it run there would
  // spend the once-per-device key before the user reaches a desktop.
  useEffect(() => {
    if (!userId || isMobile) return;

    const storageKey = `ONBOARDING_POPUP_${ONBOARDING_POPUP_TYPE.EDUCATORS}_${userId}`;
    if (localStorage.getItem(storageKey)) return;

    dispatch(
      actions.onboardingPopup.showOnboardingPopup({
        type: ONBOARDING_POPUP_TYPE.EDUCATORS,
      }),
    );
    localStorage.setItem(storageKey, 'true');
  }, [userId, isMobile, dispatch]);

  // The popup owns the answers and reports back only how it ended, so the page
  // can record the outcome without knowing what was filled in.
  const handleCloseSurvey = (status) =>
    dispatch(actions.educatorsSurvey.hideEducatorsSurvey(status));

  // Educators only runs on desktop, so phones get a screen saying so rather
  // than a home page none of whose tools they could use.
  if (isMobile) return <MobileWeb />;

  return (
    <StyledEducatorsHome>
      <Header />
      <div className="educators-content">
        <div className="educators-sections">
          <Banner />
          <Features />
          <TeachingVoices />
          <RecentProjects />
        </div>
      </div>

      <SurveyPopup open={isVisible} onClose={handleCloseSurvey} />
    </StyledEducatorsHome>
  );
};

export default EducatorsHome;
